type CloudRow = { label: string; value: string };

const layoutForRole = (role: string, order: number) => {
  const layout = { container: false, lane: 'network', rank: 3, order };
  switch (role) {
    case 'transit_hub':
    case 'vpn_connection':
      return { ...layout, lane: 'connectivity', rank: 1 };
    case 'transit_attachment':
      return { ...layout, lane: 'connectivity', rank: 2 };
    case 'network':
      return { ...layout, container: true, rank: 1 };
    case 'availability_zone':
      return { ...layout, container: true, rank: 2 };
    case 'subnet':
      return { ...layout, container: true, rank: 3 };
    case 'internet_gateway':
    case 'nat_gateway':
    case 'compute_instance':
      return { ...layout, lane: 'workload', rank: 4 };
    case 'network_interface':
    case 'public_ip':
      return { ...layout, lane: 'workload', rank: 5 };
    default:
      return layout;
  }
};

const cloudNode = (
  id: string,
  role: string,
  title: string,
  subtitle: string,
  parentId: string,
  rows: CloudRow[],
  order = 0
) => ({
  id,
  displayName: title,
  labels: { family: 'cloud', domain: 'aws' },
  properties: {
    presentation: {
      title,
      subtitle,
      rows,
      cloud: {
        version: '1',
        nodeRole: role,
        origin: role === 'availability_zone' ? 'derived' : 'graph',
        parentId,
        layout: layoutForRole(role, order)
      }
    }
  }
});

const cloudEdge = (id: string, fromId: string, toId: string, edgeRole: 'hierarchy' | 'attachment' | 'peering') => ({
  id,
  fromId,
  toId,
  directed: true,
  labels: {
    family: 'cloud',
    domain: 'aws',
    relation: edgeRole === 'hierarchy' ? 'contains' : 'connects'
  },
  properties: {
    presentation: {
      cloud: { edgeRole, sourceGraphEdgeIds: [id] }
    }
  }
});

// Intentionally small and curated, rather than a captured deployment payload.
// It covers the supported relationships: VPN -> TGW -> attachment -> VPC ->
// AZ -> subnet -> owner/ENI/EIP, NAT/IGW, a direct ENI, a detached VPC, and
// VPC peering.
const cloudConnectivityMock = {
  graph: {
    graphId: 'mock:aws_cloud_overview:site:aws-use1',
    generatedAt: '2026-09-02T00:00:00.000Z',
    scope: { scopeType: 'site', scopeId: 'aws-use1' },
    meta: {
      presentation: {
        provider: 'aws',
        renderer: 'cloud_connectivity',
        source: 'topology_service'
      }
    },
    nodes: [
      cloudNode('vpn:primary', 'vpn_connection', 'Primary site VPN', 'vpn-0a1b2c3d', '', [
        { label: 'VPN connection ID', value: 'vpn-0a1b2c3d' },
        { label: 'State', value: 'available' }
      ]),
      cloudNode('tgw:main', 'transit_hub', 'core-transit-gateway', 'tgw-0123456789', '', [
        { label: 'Transit gateway ID', value: 'tgw-0123456789' },
        { label: 'State', value: 'available' }
      ]),
      cloudNode('attachment:vpn', 'transit_attachment', 'VPN attachment', 'tgw-attach-vpn', 'tgw:main', [
        { label: 'Attachment ID', value: 'tgw-attach-vpn' },
        { label: 'Resource type', value: 'vpn' }
      ]),
      cloudNode('attachment:application', 'transit_attachment', 'Application VPC attachment', 'tgw-attach-app', 'tgw:main', [
        { label: 'Attachment ID', value: 'tgw-attach-app' },
        { label: 'Resource type', value: 'vpc' }
      ]),
      cloudNode('vpc:application', 'network', 'application-vpc', 'vpc-0abc1234', '', [
        { label: 'VPC ID', value: 'vpc-0abc1234' },
        { label: 'CIDR', value: '10.20.0.0/16' },
        { label: 'State', value: 'available' }
      ]),
      cloudNode('az:application:use1-az1', 'availability_zone', 'us-east-1a', 'Availability Zone', 'vpc:application', [
        { label: 'Availability Zone', value: 'us-east-1a' },
        { label: 'Availability Zone ID', value: 'use1-az1' }
      ]),
      cloudNode('subnet:application-public', 'subnet', 'application-public-a', 'subnet-0a1b2c3d', 'az:application:use1-az1', [
        { label: 'Subnet ID', value: 'subnet-0a1b2c3d' },
        { label: 'CIDR', value: '10.20.1.0/24' },
        { label: 'Availability Zone', value: 'us-east-1a' }
      ]),
      cloudNode('ec2:web', 'compute_instance', 'web-01', 'i-0123456789', 'subnet:application-public', [
        { label: 'Instance ID', value: 'i-0123456789' },
        { label: 'Type', value: 't3.medium' },
        { label: 'State', value: 'running' },
        { label: 'Private IP', value: '10.20.1.10' }
      ]),
      cloudNode('eni:web', 'network_interface', 'web-01-eni', 'eni-0a1b2c3d', 'ec2:web', [
        { label: 'Network interface ID', value: 'eni-0a1b2c3d' },
        { label: 'Private IP', value: '10.20.1.10' },
        { label: 'Status', value: 'in-use' }
      ]),
      cloudNode('eip:web', 'public_ip', 'web-01-public-ip', '198.51.100.20', 'eni:web', [
        { label: 'Allocation ID', value: 'eipalloc-0123456789' },
        { label: 'Public IP', value: '198.51.100.20' }
      ]),
      cloudNode('eni:direct', 'network_interface', 'inspection-eni', 'eni-0d4e5f6a', 'subnet:application-public', [
        { label: 'Network interface ID', value: 'eni-0d4e5f6a' },
        { label: 'Private IP', value: '10.20.1.50' },
        { label: 'Status', value: 'in-use' }
      ], 1),
      cloudNode('nat:application', 'nat_gateway', 'application-nat-a', 'nat-0123456789', 'subnet:application-public', [
        { label: 'NAT gateway ID', value: 'nat-0123456789' },
        { label: 'State', value: 'available' }
      ], 2),
      cloudNode('eni:nat', 'network_interface', 'application-nat-eni', 'eni-0f1e2d3c', 'nat:application', [
        { label: 'Network interface ID', value: 'eni-0f1e2d3c' },
        { label: 'Private IP', value: '10.20.1.5' }
      ]),
      cloudNode('igw:application', 'internet_gateway', 'application-igw', 'igw-0123456789', 'vpc:application', [
        { label: 'Internet gateway ID', value: 'igw-0123456789' },
        { label: 'State', value: 'available' }
      ]),
      cloudNode('vpc:shared', 'network', 'shared-services-vpc', 'vpc-0def5678', '', [
        { label: 'VPC ID', value: 'vpc-0def5678' },
        { label: 'CIDR', value: '10.30.0.0/16' },
        { label: 'State', value: 'available' }
      ], 1),
      cloudNode('az:shared:use1-az2', 'availability_zone', 'us-east-1b', 'Availability Zone', 'vpc:shared', [
        { label: 'Availability Zone', value: 'us-east-1b' },
        { label: 'Availability Zone ID', value: 'use1-az2' }
      ]),
      cloudNode('subnet:shared-private', 'subnet', 'shared-private-b', 'subnet-0e4f5a6b', 'az:shared:use1-az2', [
        { label: 'Subnet ID', value: 'subnet-0e4f5a6b' },
        { label: 'CIDR', value: '10.30.10.0/24' },
        { label: 'Availability Zone', value: 'us-east-1b' }
      ]),
      cloudNode('eni:resolver', 'network_interface', 'resolver-eni', 'eni-0b2c4d6e', 'subnet:shared-private', [
        { label: 'Network interface ID', value: 'eni-0b2c4d6e' },
        { label: 'Private IP', value: '10.30.10.53' },
        { label: 'Status', value: 'in-use' }
      ])
    ],
    edges: [
      cloudEdge('hierarchy:tgw-vpn-attachment', 'tgw:main', 'attachment:vpn', 'hierarchy'),
      cloudEdge('hierarchy:tgw-application-attachment', 'tgw:main', 'attachment:application', 'hierarchy'),
      cloudEdge('attachment:vpn', 'attachment:vpn', 'vpn:primary', 'attachment'),
      cloudEdge('attachment:application', 'attachment:application', 'vpc:application', 'attachment'),
      cloudEdge('hierarchy:application-zone', 'vpc:application', 'az:application:use1-az1', 'hierarchy'),
      cloudEdge('hierarchy:application-subnet', 'az:application:use1-az1', 'subnet:application-public', 'hierarchy'),
      cloudEdge('hierarchy:application-web', 'subnet:application-public', 'ec2:web', 'hierarchy'),
      cloudEdge('hierarchy:web-eni', 'ec2:web', 'eni:web', 'hierarchy'),
      cloudEdge('hierarchy:web-eip', 'eni:web', 'eip:web', 'hierarchy'),
      cloudEdge('hierarchy:direct-eni', 'subnet:application-public', 'eni:direct', 'hierarchy'),
      cloudEdge('hierarchy:application-nat', 'subnet:application-public', 'nat:application', 'hierarchy'),
      cloudEdge('hierarchy:nat-eni', 'nat:application', 'eni:nat', 'hierarchy'),
      cloudEdge('hierarchy:application-igw', 'vpc:application', 'igw:application', 'hierarchy'),
      cloudEdge('hierarchy:shared-zone', 'vpc:shared', 'az:shared:use1-az2', 'hierarchy'),
      cloudEdge('hierarchy:shared-subnet', 'az:shared:use1-az2', 'subnet:shared-private', 'hierarchy'),
      cloudEdge('hierarchy:resolver-eni', 'subnet:shared-private', 'eni:resolver', 'hierarchy'),
      cloudEdge('peering:application-shared', 'vpc:application', 'vpc:shared', 'peering')
    ]
  },
  legend: { edgeRules: [], nodeRules: [] },
  drilldowns: [],
  meta: {
    context: {
      'cloud.accountId': '123456789012',
      'cloud.region': 'us-east-1'
    }
  }
};

export const buildMockCloudConnectivity = (scopeType: string, scopeId: string) => {
  const fixture = structuredClone(cloudConnectivityMock);
  fixture.graph.graphId = `mock:aws_cloud_overview:${scopeType}:${scopeId}`;
  fixture.graph.generatedAt = new Date().toISOString();
  fixture.graph.scope = { scopeType, scopeId };
  return fixture;
};
